import { Node, mergeAttributes } from '@tiptap/core'
import { Plugin, PluginKey } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'

export interface MoreButtonOptions {
    HTMLAttributes: Record<string, any>
    onClick?: (event: { buttonId: string; instanceId: string; target: HTMLElement }) => void
}

export const MoreButtonExtension = Node.create<MoreButtonOptions>({
    name: 'moreButton',

    group: 'inline',
    inline: true,
    atom: true, // Node is treated as a single unit

    addOptions() {
        return {
            HTMLAttributes: {},
            onClick: undefined,
        }
    },

    addAttributes() {
        return {
            buttonId: {
                default: 'expand-button',
                parseHTML: element => element.getAttribute('data-button-id'),
                renderHTML: attributes => {
                    return {
                        'data-button-id': attributes.buttonId,
                    }
                },
            },
            instanceId: {
                default: 'unknown',
                parseHTML: element => element.getAttribute('data-instance-id'),
                renderHTML: attributes => {
                    return {
                        'data-instance-id': attributes.instanceId,
                    }
                },
            },
        }
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-more-button]',
            },
        ]
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'span',
            mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
                'data-more-button': 'true',
                class: 'more-button',
                contenteditable: 'false',
                tabindex: '-1',
                style: [
                    'color: rgb(156 147 255)',
                    'font-weight: 600',
                    'cursor: pointer',
                    'display: inline-block',
                    'user-select: none',
                    'outline: none',
                    '-webkit-tap-highlight-color: transparent',
                ].join('; '),
            }),
            'more',
        ]
    },

    addNodeView() {
        return ({ node, HTMLAttributes }) => {
            const dom = document.createElement('span')

            // Apply all attributes
            Object.entries({
                ...this.options.HTMLAttributes,
                ...HTMLAttributes,
                'data-more-button': 'true',
                'data-button-id': node.attrs.buttonId,
                'data-instance-id': node.attrs.instanceId,
                class: 'more-button',
                contenteditable: 'false',
                tabindex: '-1',
            }).forEach(([key, value]) => {
                dom.setAttribute(key, value)
            })

            // Apply styles
            dom.style.color = 'rgb(156 147 255)'
            dom.style.fontWeight = '600'
            dom.style.cursor = 'pointer'
            dom.style.display = 'inline-block'
            dom.style.userSelect = 'none'
            dom.style.outline = 'none'
            dom.style.webkitTapHighlightColor = 'transparent'

            dom.textContent = 'more'

            // Handle click/touch events
            const handleAction = (e: Event) => {
                e.preventDefault()
                e.stopPropagation()

                const eventDetail = {
                    buttonId: node.attrs.buttonId || 'expand-button',
                    instanceId: node.attrs.instanceId || 'unknown',
                    target: dom,
                }

                // Dispatch to document for compatibility with existing code
                document.dispatchEvent(
                    new CustomEvent('more-button-clicked', {
                        bubbles: true,
                        detail: eventDetail,
                    }),
                )

                // Also call the onClick handler if provided
                if (this.options.onClick) {
                    this.options.onClick(eventDetail)
                }
            }

            dom.addEventListener('click', handleAction)
            dom.addEventListener('touchstart', handleAction)

            return {
                dom,
                // Prevent content updates since this is an atom node
                update: () => false,
                // Clean up event listeners when node is destroyed
                destroy: () => {
                    dom.removeEventListener('click', handleAction)
                    dom.removeEventListener('touchstart', handleAction)
                },
            }
        }
    },

    addCommands() {
        return {
            insertMoreButton:
                (attributes?: { buttonId?: string; instanceId?: string }) =>
                ({ commands }) => {
                    return commands.insertContent({
                        type: this.name,
                        attrs: attributes,
                    })
                },
        }
    },
})

// Export a helper to check if content has a more button
export const hasMoreButton = (content: any): boolean => {
    if (!content || !content.content) return false

    const checkNode = (node: any): boolean => {
        if (node.type === 'moreButton') return true
        if (node.content && Array.isArray(node.content)) {
            return node.content.some(checkNode)
        }
        return false
    }

    return content.content.some(checkNode)
}
